import {
  CalendarClock,
  Clock,
  Edit,
  FileText,
  PauseCircle,
  PlayCircle,
  ReceiptText,
  Trash2
} from 'lucide-react'
import ResizableModal from '../../../../../../ui/ResizableModal'
import { calculateProgramadoTotals } from './utils/calculateProgramadoTotals'

const EMPTY_VALUE = 'No definido'

export default function ProgramadoModalDetail({ doc, onClose, onEdit, onDelete, onToggleStatus }) {
  if (!doc) return null

  const { itemTotals, totalGeneral } = calculateProgramadoTotals(doc)
  const items = doc.details || []
  const references = doc.references || []
  const emittedInvoices =
    doc.issued_documents || doc.emitted_documents || doc.generated_documents || doc.invoices || []
  const isInactive = doc.status === 'inactive'
  const isActive = doc.status === 'active'

  const handleEditClick = () => {
    onEdit(doc)
    onClose()
  }

  const handleToggleClick = () => {
    onToggleStatus(doc, isInactive ? 'active' : 'inactive')
  }

  const formatDate = dateStr => {
    if (!dateStr) return EMPTY_VALUE
    const value = String(dateStr).split('T')[0]
    const parts = value.split('-')
    if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`
    return value
  }

  const formatMoney = value => {
    return Number(value || 0).toLocaleString('es-CL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const statusLabel = doc.status_display || (isInactive ? 'Inactiva' : isActive ? 'Activa' : doc.status)
  const frequencyLabel = doc.frequency_display || EMPTY_VALUE
  const nextExecution = formatDate(doc.next_execution)
  const docType = doc.dte_type_data?.description || doc.dte_type_display || 'Factura electronica'
  const currency = doc.currency || 'CLP'
  const endType = doc.end_type_display || 'Nunca finaliza'
  const completedOccurrences = Number(doc.completed_occurrences || 0)
  const maxOccurrences = doc.max_occurrences ? Number(doc.max_occurrences) : null
  const occurrenceText = maxOccurrences
    ? `${completedOccurrences} de ${maxOccurrences}`
    : String(completedOccurrences)
  const currencyDayText = currency === 'UF' ? `Dia ${doc.currency_day || 1} de cada mes` : null

  return (
    <ResizableModal
      open={!!doc}
      onClose={onClose}
      width={1180}
      height={760}
    >
      <div className='h-full overflow-y-auto bg-[#f8fafc]/50 p-6 custom-scrollbar'>
        <div className='overflow-hidden rounded-[24px] border border-slate-200/60 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.08)]'>
          <div className='px-5 py-4'>
            <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
              <div className='flex items-start gap-4'>
                <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-50 text-primary ring-1 ring-slate-200/60 shadow-sm'>
                  <FileText size={22} />
                </div>
                <div>
                  <h2 className='text-xl font-bold text-slate-900 mb-1.5 tracking-tight leading-none'>
                    Detalle de documento programado
                  </h2>
                  <p className='mt-0.5 max-w-2xl text-[0.72rem] font-medium leading-relaxed text-slate-500'>
                    Configuración, estado de la programación y acciones disponibles.
                  </p>
                </div>
              </div>

              <div className='flex flex-wrap gap-2 lg:justify-end'>
                <StatusChip active={isActive} label={statusLabel || EMPTY_VALUE} />
                <InfoChip icon={Clock} label={frequencyLabel} />
                <InfoChip icon={CalendarClock} label={`Proxima: ${nextExecution}`} />
              </div>
            </div>
          </div>

          <div className='grid gap-4 border-t border-slate-100/80 p-5 xl:grid-cols-[minmax(0,1fr)_300px]'>
            <div className='space-y-4'>
              <div className='grid gap-4 lg:grid-cols-2'>
                <InfoPanel
                  title='Resumen del documento'
                  eyebrow='Documento'
                  icon={ReceiptText}
                  metrics={[
                    { label: 'Tipo de documento', value: docType, emphasis: true },
                    { label: `Monto total`, value: `${formatMoney(totalGeneral)} ${currency}`, tone: 'green', featured: true },
                    { label: 'Estado actual', value: statusLabel || EMPTY_VALUE, emphasis: true },
                    { label: 'Fecha de inicio', value: formatDate(doc.start_date), secondary: true },
                    { label: 'Próxima emisión', value: nextExecution, emphasis: true, featured: true }
                  ]}
                />

                <InfoPanel
                  title='Configuracion de programacion'
                  eyebrow='Programacion'
                  icon={CalendarClock}
                  metrics={[
                    { label: 'Programacion', value: frequencyLabel, emphasis: true, featured: true },
                    { label: 'Finalizacion', value: endType },
                    ...(currencyDayText ? [{ label: 'Dia valor UF', value: currencyDayText }] : []),
                    { label: 'Emisiones completadas', value: occurrenceText, secondary: true },
                    { label: 'Creado el', value: formatDate(doc.created_at), secondary: true }
                  ]}
                />
              </div>

              <section className='rounded-[20px] border border-slate-200/50 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.04)] text-lg'>
                <SectionHeader
                  title='Ítems del documento'
                  description='Productos o servicios configurados en esta programación.'
                />

                {items.length <= 3 ? (
                  <div className='space-y-2 p-4'>
                    {items.length > 0 ? (
                      items.map((item, idx) => (
                        <div key={idx} className='flex flex-col gap-2 rounded-xl bg-slate-50/60 px-3.5 py-3 sm:flex-row sm:items-center sm:justify-between'>
                          <div className='min-w-0'>
                            <p className='truncate text-[15px] font-bold text-slate-950'>
                              {item.item_name || 'Item sin nombre'}
                            </p>
                            <p className='mt-0.5 text-xs font-medium text-slate-500'>
                              Cantidad {formatMoney(item.quantity || 0)} · {currency}
                              {currency === 'UF'
                                ? ' · Valor UF automatico'
                                : ` · $${formatMoney(item.unit_price || 0)}`}
                            </p>
                          </div>
                          <p className='shrink-0 text-base font-black text-slate-950'>
                            {formatMoney(itemTotals[idx])} {currency}
                          </p>
                        </div>
                      ))
                    ) : (
                      <EmptyInline text='No hay items configurados para esta programacion.' />
                    )}
                  </div>
                ) : (
                  <div className='overflow-x-auto'>
                    <table className='w-full border-collapse'>
                      <thead className='bg-slate-50/80 text-[0.6rem] font-bold uppercase tracking-widest text-slate-500'>
                        <tr>
                          <th className='px-4 py-3 text-left font-bold'>Item</th>
                          <th className='px-4 py-3 text-right font-bold'>Cant.</th>
                          <th className='px-4 py-3 text-right font-bold'>Precio</th>
                          <th className='px-4 py-3 text-center font-bold'>Moneda</th>
                          <th className='px-4 py-3 text-right font-bold'>Total</th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-slate-100 bg-white'>
                        {items.map((item, idx) => (
                          <tr key={idx} className='hover:bg-slate-50/70'>
                            <td className='px-4 py-3 font-semibold text-slate-800'>{item.item_name}</td>
                            <td className='px-4 py-3 text-right text-slate-600'>{formatMoney(item.quantity || 0)}</td>
                            <td className='px-4 py-3 text-right text-slate-600'>
                              {currency === 'UF' ? 'Valor UF automatico' : `$${formatMoney(item.unit_price || 0)}`}
                            </td>
                            <td className='px-4 py-3 text-center text-slate-600'>{currency}</td>
                            <td className='px-4 py-3 text-right font-black text-slate-950'>
                              {formatMoney(itemTotals[idx])} {currency}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className='m-4 mt-0 flex items-center justify-between rounded-xl bg-primary px-5 py-3.5 text-white shadow-sm'>
                  <span className='text-sm font-bold uppercase'>
                    Total del documento
                  </span>
                  <span className='text-lg font-black text-white'>
                    {formatMoney(totalGeneral)} <span className='text-xs font-bold text-slate-400 ml-1'>{currency}</span>
                  </span>
                </div>
              </section>

              {references.length > 0 && (
                <section className='rounded-[20px] border border-slate-200/50 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.04)]'>
                  <SectionHeader
                    title='Referencias del documento'
                    description='Documentos relacionados incluidos en la configuración.'
                  />
                  <div className='overflow-x-auto'>
                    <table className='w-full border-collapse'>
                      <thead className='bg-slate-50/80 text-[0.6rem] font-bold uppercase tracking-widest text-slate-500'>
                        <tr>
                          <th className='px-5 py-3.5 text-left font-bold'>Tipo</th>
                          <th className='px-5 py-3.5 text-center font-bold'>Folio</th>
                          <th className='px-5 py-3.5 text-center font-bold'>Fecha</th>
                          <th className='px-5 py-3.5 text-left font-bold'>Razon</th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-slate-100'>
                        {references.map((ref, idx) => (
                          <tr key={idx}>
                            <td className='px-4 py-3'>
                              <p className='font-semibold text-slate-800'>
                                {getReferenceName(ref)}
                              </p>
                              <p className='text-[10px] font-medium text-slate-400'>
                                Codigo: {ref.dte_type_code || EMPTY_VALUE}
                              </p>
                            </td>
                            <td className='px-4 py-3 text-center font-semibold text-slate-600'>
                              {ref.reference_folio || EMPTY_VALUE}
                            </td>
                            <td className='px-4 py-3 text-center text-slate-600'>
                              {formatDate(ref.reference_date)}
                            </td>
                            <td className='px-4 py-3 text-slate-600'>
                              {ref.reference_reason || EMPTY_VALUE}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              <section className='text-lg rounded-[20px] border border-slate-200/50 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.04)]'>
                <SectionHeader
                  title='Facturas emitidas'
                  description='Historial asociado a esta programación.'
                />

                {emittedInvoices.length > 0 ? (
                  <div className='overflow-x-auto'>
                    <table className='w-full border-collapse'>
                      <thead className='bg-slate-50/80 text-[0.6rem] font-bold uppercase tracking-widest text-slate-500'>
                        <tr>
                          <th className='px-5 py-3.5 text-left font-bold'>Documento</th>
                          <th className='px-5 py-3.5 text-center font-bold'>Fecha</th>
                          <th className='px-5 py-3.5 text-center font-bold'>Estado</th>
                          <th className='px-5 py-3.5 text-right font-bold'>Monto</th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-slate-100'>
                        {emittedInvoices.map((invoice, idx) => (
                          <tr key={invoice.id || idx}>
                            <td className='px-4 py-3 font-semibold text-slate-800'>
                              {invoice.folio || invoice.document_number || `Emision ${idx + 1}`}
                            </td>
                            <td className='px-4 py-3 text-center text-slate-600'>
                              {formatDate(invoice.date_issued || invoice.created_at)}
                            </td>
                            <td className='px-4 py-3 text-center text-slate-600'>
                              {invoice.status_display || invoice.status || EMPTY_VALUE}
                            </td>
                            <td className='px-4 py-3 text-right font-bold text-slate-900'>
                              {formatMoney(invoice.total_amount || invoice.amount || 0)} {currency}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='flex items-center gap-3 px-5 py-4 text-left bg-slate-50/40'>
                    <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white border border-slate-200/60 text-slate-400 shadow-sm'>
                      <FileText size={16} />
                    </div>
                    <p className='text-[0.68rem] font-bold text-slate-400 uppercase tracking-wider'>
                      Aún no hay emisiones registradas.
                    </p>
                  </div>
                )}
              </section>
            </div>

            <aside className='xl:sticky xl:top-6 xl:self-start'>
              <section className='rounded-[22px] border border-slate-200/60 bg-white p-5 shadow-[0_15px_40px_rgba(15,23,42,0.06)]'>
                <div className='mb-5 border-b border-slate-100 pb-4'>
                  <h3 className='text-lg font-bold text-slate-900 mb-1.5 tracking-tight leading-none'>
                    Administrar programación
                  </h3>
                  <p className='mt-1 text-[0.68rem] font-medium leading-relaxed text-slate-500'>
                    Actualiza, pausa o elimina esta configuración.
                  </p>
                </div>

                <div className='space-y-2'>
                  <button
                    type='button'
                    onClick={handleEditClick}
                    className='flex w-full items-center justify-center gap-2 rounded-xl bg-accent2 px-5 py-3 text-[0.78rem] font-bold tracking-tight text-white shadow-lg shadow-slate-200 transition hover:bg-accent2/80 active:scale-[0.98]'
                  >
                    <Edit size={16} />
                    Editar programación
                  </button>

                  <button
                    type='button'
                    onClick={handleToggleClick}
                    className='flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-[0.78rem] font-bold text-slate-600 transition hover:bg-slate-50 hover:text-slate-900 active:scale-[0.98]'
                  >
                    {isInactive ? <PlayCircle size={16} /> : <PauseCircle size={16} />}
                    {isInactive ? 'Activar programación' : 'Pausar programación'}
                  </button>

                  <div className='pt-3'>
                    <button
                      type='button'
                      onClick={() => onDelete(doc)}
                      className='mx-auto flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[0.78rem] font-bold tracking-wider text-rose-500 transition hover:bg-rose-50/50 active:scale-[0.98]'
                    >
                      <Trash2 size={13} />
                      Eliminar programación
                    </button>
                    <p className='mt-0.5 text-center max-w-2xl text-[0.72rem] font-medium leading-relaxed text-slate-500'>
                      Acción permanente e irreversible.
                    </p>
                  </div>
                </div>
              </section>
            </aside>
          </div>
        </div>
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
      `}</style>
    </ResizableModal>
  )
}

function InfoPanel({ title, eyebrow, icon: Icon, metrics }) {
  return (
    <section className='rounded-2xl border border-slate-200/50 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.04)]'>
      <div className='mb-4 flex items-start justify-between gap-3'>
        <div>
          {/* <p className='text-sm mb-1.5 tracking-tight leading-none'>
            {eyebrow}
          </p> */}
          <h3 className='text-lg font-bold text-slate-900 mb-1.5 tracking-tight leading-none'>{title}</h3>
        </div>
        <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-50 text-slate-400 ring-1 ring-slate-200/60'>
          <Icon size={20} />
        </div>
      </div>

      <div className='divide-y divide-slate-100/60'>
        {metrics.map(metric => (
          <MetricRow key={metric.label} {...metric} />
        ))}
      </div>
    </section>
  )
}

function MetricRow({ label, value, featured = false }) {
  // const isEmpty = !value || value === EMPTY_VALUE
  // const valueClass = isEmpty
  //   ? 'text-slate-400 italic font-semibold'
  //   : tone === 'green'
  //     ? 'text-emerald-600'
  //     : emphasis
  //       ? 'text-slate-900 border-b border-slate-100/50 pb-0.5'
  //       : 'text-slate-600'
  // mt-0.5 max-w-2xl text-[0.72rem] font-medium leading-relaxed text-slate-500
  return (
    <div className={`flex items-start justify-between gap-2`}>
      <span className='text-xs font-medium tracking-wider leading-relaxed text-slate-500'>{label}</span>
      <span className={`max-w-[62%] text-right text-[0.78rem] font-bold`}>
        {value || EMPTY_VALUE}
      </span>
    </div>
  )
}

function SectionHeader({ title, description }) {
  return (
    <div className='border-b border-slate-200/40 px-5 py-4'>
      <h3 className='text-lg font-bold text-slate-900 mb-1.5 tracking-tight leading-none'>{title}</h3>
      <p className='mt-0.5 text-[0.68rem] font-medium leading-relaxed text-slate-500'>{description}</p>
    </div>
  )
}

function InfoChip({ icon: Icon, label }) {
  const isEmpty = !label || label.includes(EMPTY_VALUE)

  return (
    <span className={`inline-flex items-center font-bold gap-1.5 text-xs rounded-full border bg-white px-3 py-1.5 tracking-widest shadow-sm transition-all hover:bg-slate-50 ${isEmpty ? 'border-slate-100 text-slate-400' : 'border-slate-200/70 text-slate-600'}`}>
      <Icon size={13} className='text-slate-400' />
      {label || EMPTY_VALUE}
    </span>
  )
}

function StatusChip({ active, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold tracking-widest shadow-sm transition-all ${active
        ? 'border-emerald-100 bg-emerald-50/50 text-emerald-700 hover:bg-emerald-50'
        : 'border-slate-200/70 bg-slate-50/50 text-slate-600 hover:bg-slate-50'
        }`}
    >
      <StatusDot active={active} />
      {label || EMPTY_VALUE}
    </span>
  )
}

function StatusDot({ active }) {
  return (
    <span
      className={`h-2.5 w-2.5 rounded-full ${active ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]' : 'bg-slate-400'
        }`}
    />
  )
}

function EmptyInline({ text }) {
  return (
    <div className='flex items-center gap-3 py-6 px-1'>
      <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-50 border border-slate-100 text-slate-400'>
        <FileText size={20} />
      </div>
      <p className='text-[0.72rem] font-bold text-slate-400 uppercase tracking-widest'>{text}</p>
    </div>
  )
}

function getReferenceName(ref) {
  const dteNames = {
    33: 'Factura Electronica',
    34: 'Factura Electronica Exenta',
    56: 'Nota de Debito Electronica',
    61: 'Nota de Credito Electronica'
  }

  return dteNames[ref.dte_type_code] || ref.reference_type || 'Documento'
}
